/**
 * Modal
 *
 * The app's four dialogs — Save Play, Load Play, Save Defense, Load Defense — rendered through one
 * accessible implementation.
 *
 * ## What was missing
 *
 * All four were hand-rolled `createPortal` overlays with no dialog semantics at all:
 *
 *   - no `role="dialog"`, no `aria-modal`, no accessible name, so assistive tech had no idea a
 *     dialog had opened;
 *   - no focus trap and no focus restore, so Tab walked straight out of the dialog into the page
 *     behind it and focus was lost entirely when it closed;
 *   - no Escape handler — the only way out was clicking the backdrop or the × ;
 *   - a close button whose entire content was the character `×`, giving it no accessible name.
 *
 * ## The pattern
 *
 * Focus moves into the dialog on open, is confined while it is open, and returns to whatever opened
 * it on close. Escape and backdrop clicks both dismiss. `aria-modal` plus the trap tells both
 * assistive tech and the keyboard the same story.
 *
 * The class names are passed in rather than fixed so the existing per-component styling keeps
 * working — this is a behaviour change, not a restyle.
 */

import { ReactNode, useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface ModalProps {
  /** Visible dialog title. Also becomes the dialog's accessible name. */
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Class for the backdrop, e.g. `saved-plays-modal-overlay`. */
  overlayClassName: string;
  /** Class for the dialog panel, e.g. `saved-plays-modal`. */
  panelClassName: string;
}

/**
 * Everything inside the dialog that can hold focus.
 *
 * Queried live on each Tab rather than cached, because these dialogs change shape while open — the
 * Save dialog swaps between a choose step and a name-and-save step, and the Load dialog swaps
 * between loading, error, empty and list states.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function Modal({ title, onClose, children, overlayClassName, panelClassName }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  /** Whatever had focus before we opened, so it can be handed back. */
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Move focus in on open, and give it back on close.
  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const panel = panelRef.current;
    // Some dialogs autoFocus a field of their own (the play-name input does). Only take focus if
    // nothing inside has already claimed it, otherwise we would fight that.
    if (panel && !panel.contains(document.activeElement)) {
      const first = panel.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? panel).focus();
    }

    return () => {
      // Guarded: the trigger can legitimately be gone by now (deleting the last saved play removes
      // the row whose button opened the dialog).
      previouslyFocused.current?.focus?.();
    };
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;

      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        // Nothing to move to; keep focus in the dialog rather than letting it escape to the page.
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      // Wrap at both ends. Without this, Tab past the last control lands on the page behind the
      // dialog, which is still there and still scrollable.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div className={overlayClassName} onClick={onClose}>
      <div
        className={panelClassName}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // Focusable so the dialog itself can hold focus when it contains no controls yet (the Load
        // dialog's loading state), which is what keeps the trap from having nothing to trap.
        tabIndex={-1}
        onKeyDown={onKeyDown}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id={titleId}>{title}</h3>
          {/* `×` alone is not a name. */}
          <button className="modal-close" type="button" onClick={onClose} aria-label={`Close ${title}`}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="modal-content">{children}</div>
      </div>
    </div>,
    document.body
  );
}

export default Modal;
